import styled from 'styled-components';

// Table Card
export const TableCard = styled.div`
  background: ${(props) =>
    props.theme.colors?.card?.background ||
    (props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgb(248, 250, 252)')};
  border: 1px solid
    ${(props) =>
      props.theme.colors?.card?.border ||
      (props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 8px;
  box-shadow: ${(props) =>
    props.theme.colors?.card?.shadow || 'rgba(0, 0, 0, 0.1) 0px 1px 3px'};
  padding: 32px;
  margin-bottom: 32px;
  transition: box-shadow 0.15s ease;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
  width: 100%;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 768px) {
    padding: 16px;
    margin: 0 8px 0 8px;
    width: calc(100% - 16px);
    border-radius: 12px;
  }
`;

// Section Header
export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  position: relative;
`;

export const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  padding-left: 12px;
  margin-left: 8px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: -4px;
    bottom: -4px;
    width: 4px;
    background: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    border-radius: 2px;
  }
`;

export const SectionHeading = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.primary
      : props.theme.colors.blueBlack};
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
`;

export const SectionIcon = styled.div`
  width: 32px;
  height: 32px;
  background: rgba(250, 70, 22, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  position: relative;
`;

export const IconButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : 'white'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? props.theme?.colors?.['border-button'] || 'rgba(255,255,255,0.15)'
        : 'rgba(0,0,0,0.08)'};
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme?.colors?.textGray?.secondary || '#94a3b8'
      : props.theme?.colors?.textGray?.secondary || '#94A3B8'};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  text-decoration: none;
  position: relative;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.15)'
        : 'rgba(250,70,22,0.05)'};
    border-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    color: ${(props) => props.theme?.colors?.primary || '#FA4616'};

    svg {
      color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Table Container
export const TableContainer = styled.div`
  overflow-x: auto;
  margin: -32px;
  padding: 32px;

  @media (max-width: 768px) {
    margin: 0;
    padding: 0;
    width: 100%;
  }
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(0, 0, 0, 0.2)'
        : 'rgba(0, 0, 0, 0.03)'};
    border-bottom: 1px solid
      ${(props) =>
        props.theme.name === 'dark'
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(0, 0, 0, 0.1)'};
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const TableHeader = styled.th`
  text-align: left;
  padding: 16px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#475569')};

  &:nth-child(1) {
    width: 25%;
  } /* Customer */
  &:nth-child(2) {
    width: 18%;
  } /* Segment - wider for badges */
  &:nth-child(3) {
    width: 15%;
  } /* LTV */
  &:nth-child(4) {
    width: 15%;
  } /* Events YTD */
  &:nth-child(5) {
    width: 15%;
  } /* Location */
  &:nth-child(6) {
    width: 12%;
  } /* Actions */
`;

export const TableRow = styled.tr`
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb'};
  transition: all 0.15s ease;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.03)'
        : 'rgba(0,0,0,0.02)'};
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const TableCell = styled.td`
  padding: 16px;
  vertical-align: middle;
`;

// Customer Info Styles
export const CustomerName = styled.div`
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark' ? '#ffffff' : props.theme.colors.blueBlack};
  font-size: 15px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
`;

export const CustomerInfo = styled.div`
  font-size: 13px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#94a3b8'};
`;

// Badge Styles
export const Badge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;

  ${(props) => {
    switch (props.variant) {
      case 'success':
        return `
          background: ${
            props.theme.name === 'dark'
              ? props.theme?.colors?.success
                ? `${props.theme.colors.success}33`
                : 'rgba(34, 197, 94, 0.2)'
              : props.theme?.colors?.success
              ? `${props.theme.colors.success}1A`
              : 'rgba(34, 197, 94, 0.1)'
          };
          color: ${props.theme?.colors?.success || '#22C55E'};
        `;
      case 'primary':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(59, 130, 246, 0.2)'
              : 'rgba(59, 130, 246, 0.1)'
          };
          color: #3B82F6;
        `;
      case 'warning':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(220, 38, 38, 0.2)'
              : 'rgba(220, 38, 38, 0.1)'
          };
          color: #DC2626;
        `;
      case 'info':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(245, 158, 11, 0.2)'
              : 'rgba(245, 158, 11, 0.1)'
          };
          color: #F59E0B;
        `;
      case 'premium':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(139, 92, 246, 0.2)'
              : 'rgba(139, 92, 246, 0.1)'
          };
          color: #8B5CF6;
        `;
      case 'new':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(6, 182, 212, 0.2)'
              : 'rgba(6, 182, 212, 0.1)'
          };
          color: #06B6D4;
        `;
      default:
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(148, 163, 184, 0.2)'
              : 'rgba(148, 163, 184, 0.1)'
          };
          color: #94A3B8;
        `;
    }
  }}
`;

// Location Badge
export const LocationBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(148, 163, 184, 0.15)'
      : 'rgba(100, 116, 139, 0.08)'};
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(148, 163, 184, 0.2)'
        : 'rgba(100, 116, 139, 0.12)'};
`;

// Value Text
export const ValueText = styled.div`
  font-weight: 600;
  color: ${(props) => {
    // If color prop is "success", use theme success color
    if (props.color === 'success') {
      return props.theme.colors.success;
    }
    // Otherwise use provided color or default
    return (
      props.color ||
      (props.theme.name === 'dark' ? '#ffffff' : props.theme.colors.blueBlack)
    );
  }};
  font-size: 15px;
`;

// Mobile Card View - Collapsible design matching original HTML
export const MobileCardGrid = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0;
    width: 100%;
  }
`;

export const CustomerCard = styled.div`
  background: ${(props) =>
    props.theme.colors?.card?.background ||
    (props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgb(248, 250, 252)')};
  border: 1px solid
    ${(props) =>
      props.theme.colors?.card?.border ||
      (props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: ${(props) =>
    props.theme.colors?.card?.shadow || 'rgba(0, 0, 0, 0.1) 0px 1px 3px'};

  ${(props) =>
    props.expanded &&
    `
    box-shadow: ${
      props.theme.colors?.card?.shadowHover ||
      'rgba(0, 0, 0, 0.15) 0px 4px 12px'
    };
  `}
`;

export const CardHeader = styled.div`
  padding: 16px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.03)'
        : 'rgba(0,0,0,0.02)'};
  }
`;

export const CardHeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const CardCustomerInfo = styled.div`
  flex: 1;
`;

export const CardCustomerName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark' ? '#ffffff' : props.theme.colors.blueBlack};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
`;

export const CardCustomerEmail = styled.div`
  font-size: 13px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#64748b'};
`;

export const CardQuickInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const ExpandIcon = styled.svg`
  width: 20px;
  height: 20px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#64748b'};
  transition: transform 0.3s ease;

  ${(props) =>
    props.expanded &&
    `
    transform: rotate(180deg);
  `}
`;

export const CardDetails = styled.div`
  max-height: ${(props) => (props.expanded ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
  border-top: ${(props) =>
    props.expanded
      ? `1px solid ${
          props.theme.name === 'dark'
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.08)'
        }`
      : 'none'};
`;

export const CardDetailsContent = styled.div`
  padding: 16px;
`;

export const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.05)'
        : 'rgba(0,0,0,0.05)'};

  &:last-child {
    border-bottom: none;
  }
`;

export const DetailLabel = styled.span`
  font-size: 13px;
  text-transform: uppercase;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#64748b'};
  letter-spacing: 0.05em;
  font-weight: 600;
`;

export const DetailValue = styled.span`
  font-size: 15px;
  color: ${(props) =>
    props.theme.name === 'dark' ? '#ffffff' : props.theme.colors.blueBlack};
  font-weight: 500;
  text-align: right;
`;

export const CardActions = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)'};
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.08)'};
`;

export const CardActionBtn = styled.button`
  flex: 1;
  padding: 8px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : 'white'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.3)'
        : 'rgba(0,0,0,0.08)'};
  border-radius: 8px;
  color: ${(props) => (props.theme.name === 'dark' ? '#CBD5E1' : '#94A3B8')};
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-decoration: none;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    transition: color 0.15s ease;
  }

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.theme.name === 'dark' ? '#FA4616' : 'rgba(250,70,22,0.05)'};
    border-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    color: ${(props) =>
      props.theme.name === 'dark'
        ? 'white'
        : props.theme?.colors?.primary || '#FA4616'};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(250, 70, 22, 0.25);

    svg {
      color: ${(props) =>
        props.theme.name === 'dark'
          ? 'white'
          : props.theme?.colors?.primary || '#FA4616'};
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Pagination
export const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 24px;

  @media (max-width: 768px) {
    margin-top: 16px;
  }
`;

export const PageBtn = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.active
      ? props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.15)'
        : 'rgba(250,70,22,0.05)'
      : props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'white'};
  border: 1px solid
    ${(props) =>
      props.active
        ? props.theme?.colors?.primary || '#fa4616'
        : props.theme.name === 'dark'
        ? props.theme?.colors?.['border-button'] || 'rgba(255,255,255,0.15)'
        : 'rgba(0,0,0,0.08)'};
  border-radius: 6px;
  color: ${(props) =>
    props.active
      ? props.theme?.colors?.primary || '#FA4616'
      : props.theme.name === 'dark'
      ? props.theme?.colors?.textGray?.secondary || '#94a3b8'
      : props.theme?.colors?.textGray?.secondary || '#94A3B8'};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  font-size: 14px;
  font-weight: 500;

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.15)'
        : 'rgba(250,70,22,0.05)'};
    border-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const PageEllipsis = styled.span`
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#64748b'};
  margin: 0 8px;
  font-size: 14px;
`;

// Filter Overlay for mobile
export const FilterOverlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${(props) => (props.show ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    animation: fadeIn 0.3s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

// Filter Dropdown
export const FilterDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 360px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.background.primary
      : props.theme.colors?.card?.background || 'white'};
  border: 1px solid
    ${(props) =>
      props.theme.colors?.card?.border ||
      (props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 8px;
  box-shadow: ${(props) =>
    props.theme.colors?.card?.shadowHover ||
    'rgba(0, 0, 0, 0.15) 0px 4px 12px'};
  z-index: 1000;

  /* Inner content wrapper with transparent overlay */
  & > div:first-child {
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(0, 0, 0, 0.1)' : 'transparent'};
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
    overflow-x: hidden;
    border-radius: 8px;
  }

  @media (max-width: 768px) {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    top: auto !important;
    width: 100% !important;
    max-width: 100% !important;
    max-height: 80vh;
    border-radius: 16px 16px 0 0;
    z-index: 10000 !important;
    transform: none !important;
    margin: 0 !important;
    box-sizing: border-box;
    overflow: hidden;

    & > div:first-child {
      padding: 20px 16px;
      max-height: 80vh;
      overflow-y: auto;
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
    }
  }

  /* Custom scrollbar for inner content div */
  & > div:first-child::-webkit-scrollbar {
    width: 8px;
  }

  & > div:first-child::-webkit-scrollbar-track {
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#f1f1f1'};
    border-radius: 8px;
  }

  & > div:first-child::-webkit-scrollbar-thumb {
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.2)' : '#888'};
    border-radius: 8px;
  }

  & > div:first-child::-webkit-scrollbar-thumb:hover {
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.3)' : '#555'};
  }
`;

export const FilterSection = styled.div`
  margin-bottom: 20px;

  &:last-of-type {
    margin-bottom: 16px;
  }

  @media (max-width: 768px) {
    /* Add extra space for date picker calendar */
    &:has(input[type='date']) {
      margin-bottom: 24px;
    }
  }
`;

export const FilterTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: none;
  color: ${(props) => (props.theme.name === 'dark' ? '#e2e8f0' : '#334155')};
  margin-bottom: 12px;
`;

export const FilterRange = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

export const FilterInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#334155')};
  background-color: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0,0,0,0.3)' : 'white'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? props.theme?.colors?.['border-button'] || 'rgba(255,255,255,0.15)'
        : 'rgba(0,0,0,0.15)'};
  border-radius: 6px;
  transition: all 0.15s ease;

  &::placeholder {
    color: ${(props) =>
      props.theme.name === 'dark'
        ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
        : '#94a3b8'};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    box-shadow: ${(props) =>
      props.theme.name === 'dark' ? 'none' : '0 0 0 3px rgba(250,70,22,0.1)'};
  }

  @media (max-width: 768px) {
    padding: 10px 8px;
    font-size: 16px; /* Prevents zoom on iOS */
    max-width: 100%;
  }

  /* Style the calendar icon for date inputs - make whole field clickable */
  &[type='date']::-webkit-calendar-picker-indicator {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    background: none;
  }

  /* Style for date inputs */
  &[type='date'] {
    position: relative;
    color-scheme: ${(props) =>
      props.theme.name === 'dark' ? 'dark' : 'light'};
    cursor: pointer;
    /* Show native icon with better visibility */
    background-image: ${(props) =>
      props.theme.name === 'dark'
        ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z'/%3E%3C/svg%3E")`
        : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%2364748b' viewBox='0 0 16 16'%3E%3Cpath d='M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z'/%3E%3C/svg%3E")`};
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 16px;
  }
`;

export const FilterOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FilterOption = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : 'white'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.1)'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
  font-size: 14px;

  @media (hover: hover) {
    &:hover {
      background: ${(props) =>
        props.theme.name === 'dark'
          ? 'rgba(255,255,255,0.1)'
          : 'rgba(250, 70, 22, 0.05)'};
      border-color: rgba(250, 70, 22, 0.4);
    }
  }

  ${(props) =>
    props.selected &&
    `
    background: rgba(250, 70, 22, 0.3);
    border-color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
    color: ${
      props.theme.name === 'dark' ? '#ffffff' : props.theme.colors.blueBlack
    };
  `}
`;

export const FilterCheckbox = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.3)' : '#d1d5db'};
  border-radius: 4px;
  transition: all 0.15s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${(props) =>
    props.checked &&
    `
    background: ${(props) => props.theme?.colors?.primary || '#FA4616'};
    border-color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
    
    &::after {
      content: '✓';
      color: white;
      font-size: 10px;
      font-weight: bold;
    }
  `}
`;

export const FilterActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : 'transparent'};

  button {
    flex: 1;
    padding: 12px 20px;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    border: none;

    &.btn-primary {
      background: ${(props) => props.theme?.colors?.primary || '#fa4616'};
      color: #ffffff;

      &:hover {
        background: #e63e13;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(250, 70, 22, 0.3);
      }
    }

    &.btn-secondary {
      background: ${(props) =>
        props.theme.name === 'dark' ? 'rgba(255,255,255,0.08)' : 'white'};
      border: 1px solid
        ${(props) =>
          props.theme.name === 'dark'
            ? props.theme?.colors?.['border-button'] || 'rgba(255,255,255,0.15)'
            : '#e5e7eb'};
      color: ${(props) =>
        props.theme.name === 'dark'
          ? props.theme?.colors?.primary || '#FA4616'
          : props.theme.colors.blueBlack};

      &:hover {
        background: ${(props) =>
          props.theme.name === 'dark' ? 'rgba(255,255,255,0.12)' : '#f9fafb'};
        border-color: ${(props) =>
          props.theme.name === 'dark' ? 'rgba(255,255,255,0.2)' : '#d1d5db'};
      }
    }
  }
`;

// Season Pass Crown Icon Styles
export const CrownIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;
  margin-left: 8px;

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: scale(1.1);
  }
`;
